import {
  APIQueryInput,
  APIQueryOptions,
  APIQueryResult,
  useAPIQuery,
} from 'shared/api/APIQuery';
import { APIError, createAPIError } from 'shared/api/APIError';
import { APIResponse, requestCarrierAPI } from 'shared/api/CarrierAPIClient';
import { FIVE_MINUTES, ONE_DAY } from 'shared/constants/NumberConstants';
import {
  DispatcherDTO,
  DispatcherEditDTO,
  DispatcherPasswordVerificationDTO,
  DispatcherProfilePasswordDTO,
  dispatcherSchema,
} from './DispatcherDTO';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import {
  APIMutationOptions,
  APIMutationResult,
  useAPIMutation,
} from 'shared/api/APIMutation';
import { isWebView } from 'shared/constants/AppTypeConstants';
import { persistDispatcherProfile } from './dispatcherProfileStorage';

export type SendDispatcherPasswordCodeInput = Pick<
  DispatcherPasswordVerificationDTO,
  'current' | 'verification_method'
>;

export const DISPATCHER_PROFILE_CACHE_KEY: APIQueryInput = [
  'dispatchers',
  'profile',
];

export function useDispatcherProfile(
  options?: APIQueryOptions<DispatcherDTO>,
): APIQueryResult<DispatcherDTO> {
  const dispatcherData = useAPIQuery<DispatcherDTO>(
    DISPATCHER_PROFILE_CACHE_KEY,
    () => requestCarrierAPI('GET /internal/web/dispatchers/profile/'),
    {
      schema: dispatcherSchema,
      gcTime: FIVE_MINUTES,
      staleTime: FIVE_MINUTES,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      ...options,
      // Live fetch on login/mount. Do not skip because a previous session's
      // guid is in localStorage — that served the wrong dispatcher.
      enabled: !isWebView && (options?.enabled ?? true),
    },
  );

  useEffect(() => {
    const dispatcher = dispatcherData.data;

    if (dispatcher?.guid) {
      persistDispatcherProfile(dispatcher, ONE_DAY);
    }
  }, [dispatcherData.data]);

  return dispatcherData;
}

export function useDispatcher(
  guid: null | string | undefined,
  options?: APIQueryOptions<DispatcherDTO>,
): APIQueryResult<DispatcherDTO> {
  return useAPIQuery(
    ['dispatcher-detail', 'item', { guid }],
    () =>
      requestCarrierAPI(['GET /internal/web/dispatchers/{guid}/', { guid }]),
    { ...options, enabled: !!guid },
  );
}

export function useDispatcherProfileAPI() {
  const queryClient = useQueryClient();

  return useMemo(
    () => ({
      updateDispatcherProfile: (json: DispatcherEditDTO) =>
        requestCarrierAPI('PATCH /internal/web/dispatchers/profile/', {
          json,
        }).then((response: APIResponse<DispatcherDTO>) => {
          void queryClient.setQueryData(
            DISPATCHER_PROFILE_CACHE_KEY,
            () => response.data,
          );

          if (response.data.guid) {
            persistDispatcherProfile(response.data, ONE_DAY);
          }

          return response;
        }),

      updateDispatcherPassword: (
        json: DispatcherProfilePasswordDTO | DispatcherPasswordVerificationDTO,
      ) =>
        requestCarrierAPI('PATCH /internal/web/dispatchers/reset-password/', {
          json,
        }),

      sendDispatcherPasswordCode: (json: SendDispatcherPasswordCodeInput) =>
        requestCarrierAPI(
          'POST /internal/web/dispatchers/reset-password/send-code/',
          { json },
        ).catch((error: APIError) => {
          throw error.type === 'TooManyRequests'
            ? createAPIError({
                ...error,
                message: 'Too many code requests. Please try again later.',
              })
            : error;
        }),
    }),
    [queryClient],
  );
}

export const useSendDispatcherPasswordCodeMutation = (
  options?: APIMutationOptions<SendDispatcherPasswordCodeInput>,
): APIMutationResult<SendDispatcherPasswordCodeInput> => {
  const { sendDispatcherPasswordCode } = useDispatcherProfileAPI();
  return useAPIMutation(
    (values: SendDispatcherPasswordCodeInput) =>
      sendDispatcherPasswordCode(values),
    options,
  );
};

export const useUpdateDispatcherProfileMutation = (
  options?: APIMutationOptions<DispatcherEditDTO>,
): APIMutationResult<DispatcherEditDTO> => {
  const { updateDispatcherProfile } = useDispatcherProfileAPI();
  return useAPIMutation(
    (values: DispatcherEditDTO) => updateDispatcherProfile(values),
    options,
  );
};
